import { ContentHeader } from "@components/contentHeader/contentHeader";
import { EditorField } from "@components/field/editorField";
import { Field } from "@components/field/field";
import { TranslationTabs } from "@components/translations/translationTabs/translationTabs";
import { AmbassadorConfigFormValues } from "@pages/addAmbassadorConfigPage/addAmbassadorConfigPage";
import { SwitchWrapper } from "@pages/addGalleriesPage/addGalleriesPageStyle";
import { languages } from "@pages/translationsPage/constants/languages";
import { LanguageErrors } from "@utils/errorsToLanguageErrors";
import { AmbassadorConfigLanguageFieldProps } from "@utils/languageFieldProps";
import { maxCharacters } from "@utils/maxCharacters";
import { Editor_admin } from "@utils/roles";
import { LanguageObject } from "@utils/toLanguageObject";
import { useHasRole } from "@utils/useHasRole";
import { Card, Switch } from "antd";
import Form, { FormInstance } from "antd/lib/form";
import * as React from "react";
import { FormattedMessage } from "react-intl";

export interface ContentFormValues {
    title: LanguageObject;
    intro: LanguageObject;
    ambassadorIntro: LanguageObject;
    caption: LanguageObject;
    withAdvancedMode?: boolean;
}

export interface ContentCardProps {
    form: FormInstance;
    hidden?: boolean;
    disabled?: boolean;
    activeLanguage: string;
    languageErrors?: LanguageErrors;
    defaultValues: AmbassadorConfigFormValues;
    onBlur(): void;
    changeSelectedLanguage(languageCode: string): void;
}

const titleField: React.FC<AmbassadorConfigLanguageFieldProps> = (props) => (
    <Field
        key={`title.${props.language}`}
        hidden={props.hidden}
        id={`title.${props.language}`}
        info={maxCharacters()}
        label={<FormattedMessage id="title" />}
        name={["title", props.language]}
        rules={[
            {
                max: 140,
                message: <FormattedMessage id="form.toolong" />
            },
            {
                required: true,
                message: <FormattedMessage id="form.isrequired" />
            }
        ]}
        {...props.sharedProps}
    />
);

const introField = (props: AmbassadorConfigLanguageFieldProps) => (
    <EditorField
        key={`intro.${props.language}`}
        disabled={props.sharedProps.disabled}
        form={props.sharedProps.form}
        hidden={props.hidden}
        id={`intro.${props.language}`}
        info={maxCharacters()}
        isAdvancedMode={props.defaultValues.withAdvancedMode ?? false}
        label={<FormattedMessage id="intro" />}
        name={["intro", props.language]}
        value={props.defaultValues.intro && props.defaultValues.intro[props.language]}
        onBlur={props.sharedProps.onBlur}
        onChangeUnsaved={props.sharedProps.onChange}
    />
);

const ambassadorIntroField = (props: AmbassadorConfigLanguageFieldProps) => (
    <EditorField
        key={`ambassadorIntro.${props.language}`}
        disabled={props.sharedProps.disabled}
        form={props.sharedProps.form}
        hidden={props.hidden}
        id={`ambassadorIntro.${props.language}`}
        info={maxCharacters()}
        isAdvancedMode={props.defaultValues.withAdvancedMode ?? false}
        label={<FormattedMessage id="ambassadorIntro" />}
        name={["ambassadorIntro", props.language]}
        value={props.defaultValues.ambassadorIntro && props.defaultValues.ambassadorIntro[props.language]}
        onBlur={props.sharedProps.onBlur}
        onChangeUnsaved={props.sharedProps.onChange}
    />
);

const captionField = (props: AmbassadorConfigLanguageFieldProps) => (
    <Field
        key={`caption.${props.language}`}
        hidden={props.hidden}
        id={`caption.${props.language}`}
        info={maxCharacters()}
        label={<FormattedMessage id="caption" />}
        name={["caption", props.language]}
        rules={[
            {
                required: true,
                message: <FormattedMessage id="form.isrequired" />
            }
        ]}
        {...props.sharedProps}
    />
);

export const ContentCard = React.memo<ContentCardProps>(({
    changeSelectedLanguage,
    hidden,
    languageErrors,
    disabled,
    defaultValues,
    activeLanguage,
    form,
    onBlur
}) => {
    const sharedProps = {
        disabled,
        form,
        onBlur
    };

    const langFieldProps = languages.map(language => ({
        sharedProps,
        defaultValues,
        language: language.code,
        hidden: language.code !== activeLanguage
    }));

    const hasRole = useHasRole();
    const isEditorAdmin = hasRole(Editor_admin);
    const [withAdvancedMode, setWithAdvancedMode] = React.useState(defaultValues.withAdvancedMode ?? false);

    return (
        <Card hidden={hidden}>
            <ContentHeader
                title={<FormattedMessage id="content" />}
            />
            <TranslationTabs
                activeLanguage={activeLanguage}
                errors={languageErrors}
                handleChangeSelectedLanguage={changeSelectedLanguage}
                languages={languages}
            />
            {langFieldProps.map(titleField)}
            {langFieldProps.map(captionField)}
            {isEditorAdmin && (
                <SwitchWrapper
                    direction="column"
                    margin="20px 0 0 0"
                >
                    <Form.Item
                        name={["withAdvancedMode"]}
                        valuePropName="checked"
                    >
                        <Switch
                            {...sharedProps}
                            checkedChildren={<FormattedMessage id="editor.isAdvancedMode" />}
                            unCheckedChildren={<FormattedMessage id="editor.isBasicMode" />}
                            onChange={(value) => setWithAdvancedMode(value)}
                        />
                    </Form.Item>
                </SwitchWrapper>
            )}
            {langFieldProps.map((fieldProps) => introField({ ...fieldProps, defaultValues: { ...defaultValues, withAdvancedMode } }))}
            {langFieldProps.map((fieldProps) => ambassadorIntroField({ ...fieldProps, defaultValues: { ...defaultValues, withAdvancedMode } }))}
        </Card>
    );
});
